import axios from "axios";
import { getRuntimeStore } from "../store/runtime";
import { refresh_token, resolveAccessContext } from "./auth";
import { normalizeTokenPayload } from "./tokenUtils";

const authURL = "/auth";
export { authURL };

const userAuthURL = "/user-auth";
export { userAuthURL };

const adminBaseURL = "/admin";
export { adminBaseURL };

const businessBaseURL = "/businesses";
export { businessBaseURL };

const clientBaseURL = "/clients";
export { clientBaseURL };

const userBaseURL = "/auth";
export { userBaseURL };

const overviewBaseURL = "/dashboard-api";
export { overviewBaseURL };

const auths = axios.create({
  baseURL: authURL,
});

const userAuth = axios.create({
  baseURL: userAuthURL,
});

const admin = axios.create({
  baseURL: adminBaseURL,
});

const business = axios.create({
  baseURL: businessBaseURL,
});

const client = axios.create({
  baseURL: clientBaseURL,
});

const user = axios.create({
  baseURL: userBaseURL,
});

const overView = axios.create({
  baseURL: overviewBaseURL,
});

let refreshPromise = null;
const LOGOUT_ACTION_TYPE = "auth/logout";
const REFRESH_TOKEN_ACTION_TYPE = "auth/setRefreshedTokenData";

const unwrapRefreshResponse = (response) => {
  const data = response?.data;

  if (typeof data?.statusCodeValue === "number") {
    if (data.statusCodeValue >= 400) {
      const error = new Error(
        data.body?.error || data.body || "Unable to refresh session"
      );
      error.statusCode = data.statusCodeValue;
      error.body = data.body || null;
      throw error;
    }

    return data.body;
  }

  return data;
};

const refreshAuthSession = async () => {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    const store = getRuntimeStore();
    const refreshToken = store?.getState()?.auth?.refreshToken;
    if (!refreshToken) {
      store?.dispatch({ type: LOGOUT_ACTION_TYPE });
      throw new Error("Missing refresh token");
    }

    const response = await refresh_token(refreshToken);
    let refreshedTokenData;
    try {
      refreshedTokenData = unwrapRefreshResponse(response);
    } catch (error) {
      store?.dispatch({ type: LOGOUT_ACTION_TYPE });
      throw error;
    }

    const payload = normalizeTokenPayload(refreshedTokenData);
    if (!payload.accessToken) {
      store?.dispatch({ type: LOGOUT_ACTION_TYPE });
      throw new Error("Missing refreshed access token");
    }

    const accessContext = await resolveAccessContext(payload.accessToken).catch(
      () => null
    );

    store?.dispatch({
      type: REFRESH_TOKEN_ACTION_TYPE,
      payload: {
        ...payload,
        accessContext,
      },
    });

    return payload;
  })();

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
};

const attachRefreshInterceptor = (apiClient) => {
  apiClient.interceptors.response.use(
    async (response) => {
      const originalRequest = response.config;

      if (response?.data?.statusCodeValue === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          await refreshAuthSession();
          return apiClient(originalRequest);
        } catch (refreshError) {
          return Promise.reject(response?.data?.body || refreshError);
        }
      }

      return response;
    },
    async (error) => {
      const originalRequest = error.config || {};

      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          await refreshAuthSession();
          return apiClient(originalRequest);
        } catch (refreshError) {
          getRuntimeStore()?.dispatch({ type: LOGOUT_ACTION_TYPE });
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    }
  );
};

[admin, business, client, user, userAuth, overView].forEach(attachRefreshInterceptor);

export { admin, auths, business, client, user, userAuth, overView };
